'use client'

import { Loader } from '@/components/ai-elements/loader'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import type { ToolUIPart } from 'ai'
import {
	CheckCircleIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	CircleIcon,
	ClockIcon,
	WrenchIcon,
	XCircleIcon,
} from 'lucide-react'
import type { ComponentProps, JSX, ReactNode } from 'react'
import { isValidElement } from 'react'
import { CodeBlock } from './code-block'

export type ToolProps = ComponentProps<typeof Collapsible>

export const Tool = ({ className, ...props }: ToolProps) => (
	<Collapsible
		className={cn('not-prose my-3 w-full rounded-md border', className)}
		{...props}
	/>
)

export type ToolHeaderProps = {
	title?: string
	type: ToolUIPart['type']
	state: ToolUIPart['state']
	className?: string
	icon?: JSX.Element | React.ComponentType<{ className?: string }>
	showChevron?: boolean
}

const getStatusBadge = (status: ToolUIPart['state']) => {
	// Use string Record type to support AI SDK v6 human-in-the-loop states
	// ('approval-requested', 'approval-responded', 'output-denied') that aren't in v5.
	// TODO: Switch back to Record<ToolUIPart['state'], string> when upgrading to AI SDK v6
	const labels: Record<string, string> = {
		'input-streaming': 'Pending',
		'input-available': 'Running',
		'approval-requested': 'Awaiting Approval',
		'approval-responded': 'Responded',
		'output-available': 'Completed',
		'output-error': 'Error',
		'output-denied': 'Denied',
	}

	const icons: Record<string, ReactNode> = {
		'input-streaming': <CircleIcon className="size-4" />,
		'input-available': <Loader size={10} />,
		'approval-requested': <ClockIcon className="size-4 text-yellow-600" />,
		'approval-responded': <CheckCircleIcon className="size-4 text-blue-600" />,
		'output-available': <CheckCircleIcon className="size-4 text-green-600" />,
		'output-error': <XCircleIcon className="size-4 text-red-600" />,
		'output-denied': <XCircleIcon className="size-4 text-orange-600" />,
	}

	return (
		<Badge className="gap-1.5 rounded-full text-xs" variant="secondary">
			{icons[status]}
			{labels[status]}
		</Badge>
	)
}

export const ToolHeader = ({
	className,
	title,
	type,
	state,
	icon,
	showChevron = true,
	...props
}: ToolHeaderProps) => {
	let iconElement: React.ReactNode

	if (isValidElement(icon)) {
		iconElement = icon
	} else if (icon) {
		const IconComponent = icon as React.ComponentType<{ className?: string }>
		iconElement = <IconComponent className="size-4 text-muted-foreground" />
	} else {
		iconElement = <WrenchIcon className="size-4 text-muted-foreground" />
	}

	return (
		<CollapsibleTrigger
			className={cn(
				'flex w-full items-center justify-between gap-4 p-3',
				className,
			)}
			{...props}
		>
			<div className="flex items-center gap-2 w-full">
				{iconElement}
				<span className="font-medium text-sm text-left grow">
					{title ?? type.split('-').slice(1).join('-')}
				</span>
				{getStatusBadge(state)}
			</div>
			{showChevron && (
				<ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
			)}
		</CollapsibleTrigger>
	)
}

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
	<CollapsibleContent
		className={cn(
			'data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in',
			className,
		)}
		{...props}
	/>
)

export type ToolInputProps = ComponentProps<'div'> & {
	input: ToolUIPart['input']
}

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => (
	<div className={cn('space-y-2 overflow-hidden p-4', className)} {...props}>
		<h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
			Parameters
		</h4>
		<div className="rounded-md bg-muted/50">
			<CodeBlock code={JSON.stringify(input, null, 2)} language="json" />
		</div>
	</div>
)

export type ToolOutputProps = ComponentProps<'div'> & {
	output: ToolUIPart['output']
	errorText: ToolUIPart['errorText']
}

const formatOutputText = (text: string): ReactNode => {
	const lines = text.split('\n')
	const firstNonEmptyIndex = lines.findIndex(l => l.trim().length > 0)

	return (
		<div className="space-y-1">
			{lines.map((line, i) => {
				const trimmed = line.trim()
				if (!trimmed) { return null }

				// Question headers (e.g. '1. "Question text..." (type: select)')
				const questionMatch = trimmed.match(/^(\d+)\.\s+"(.+?)"\s+\(type:\s+(\w+)\)$/)
				if (questionMatch) {
					return (
						<div key={i} className="pt-2 first:pt-0">
							<span className="font-semibold text-foreground">{questionMatch[1]}.</span>{' '}
							<span className="text-foreground">{questionMatch[2]}</span>
							<span className="ml-1.5 text-muted-foreground text-[10px] uppercase">
								{questionMatch[3]}
							</span>
						</div>
					)
				}

				// SYSTEM questions (e.g. '9. [SYSTEM — this question cannot be modified or removed]')
				if (/^\d+\.\s+\[SYSTEM/.test(trimmed)) {
					const num = trimmed.match(/^(\d+)\./)?.[1]
					return (
						<div key={i} className="pt-2 first:pt-0 text-muted-foreground italic text-[11px]">
							<span className="font-semibold not-italic">{num}.</span> System field (contact info)
						</div>
					)
				}

				// Section label (e.g. 'Options:')
				if (trimmed === 'Options:') {
					return (
						<div key={i} className="text-muted-foreground text-[10px] uppercase tracking-wide pl-4 pt-0.5">
							Options
						</div>
					)
				}

				// Option lines (e.g. '- "Option text" (score: 3)')
				const optionMatch = trimmed.match(/^-\s+"(.+?)"\s+\(score:\s*(\d+)\)(\s+\[DISQUALIFYING\])?$/)
				if (optionMatch) {
					return (
						<div key={i} className="flex items-baseline gap-1.5 pl-5 text-[12px]">
							<span className="text-muted-foreground shrink-0">-</span>
							<span className="text-foreground">{optionMatch[1]}</span>
							<span className="ml-auto shrink-0 text-[10px] tabular-nums text-muted-foreground">
								{optionMatch[2]}pt
							</span>
							{optionMatch[3] && <span className="text-[9px] text-destructive font-medium">DQ</span>}
						</div>
					)
				}

				// Header line (e.g. 'Current questionnaire questions:')
				if (trimmed.endsWith(':') && i === firstNonEmptyIndex) {
					return (
						<div
							key={i}
							className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide pb-1"
						>
							{trimmed.replace(/:$/, '')}
						</div>
					)
				}

				// Fallback
				return <div key={i} className="text-foreground text-[12px] pl-1">{trimmed}</div>
			})}
		</div>
	)
}

export const ToolOutput = ({
	className,
	output,
	errorText,
	...props
}: ToolOutputProps) => {
	if (!(output || errorText)) {
		return null
	}

	const isTextOutput = typeof output === 'string' && !(() => {
		try {
			JSON.parse(output as string)
			return true
		} catch {
			return false
		}
	})()

	return (
		<div className={cn('p-3 pt-0', className)} {...props}>
			<div
				className={cn(
					'overflow-x-auto rounded-md text-xs max-h-[400px] overflow-y-auto',
					errorText
						? 'bg-destructive/10 text-destructive p-3'
						: 'bg-muted/30 p-3',
				)}
			>
				{errorText && <div>{errorText}</div>}
				{!errorText && isTextOutput && formatOutputText(output as string)}
				{!errorText && !isTextOutput && (
					<pre className="whitespace-pre-wrap break-words text-[12px] font-mono">
						{typeof output === 'string' ? output : JSON.stringify(output, null, 2)}
					</pre>
				)}
			</div>
			<CollapsibleTrigger className="flex items-center justify-center w-full pt-1.5 pb-0.5 text-muted-foreground hover:text-foreground transition-colors">
				<ChevronUpIcon className="size-3.5" />
				<span className="text-[11px] ml-1">Collapse</span>
			</CollapsibleTrigger>
		</div>
	)
}
